import { HanRoleColumns, HanRoleData, hanRoleEntityName } from '@/backend/config/classifiers';
import { WidgetFormConfig } from '@juvare/common-form-ui/WidgetForm';
import { ClassifierStatus } from '@juvare/platform-sdk-react/backend/features/classifiers/shared';
import { ClassifierAdminPage, ClassifierAdminPageCsvRow, SlideoutFields } from '@juvare/platform-sdk-react/ui/features/classifiers';
import { usePlatformSdkReactTranslation } from '@juvare/platform-sdk-react/ui/hooks/usePlatformSdkReactTranslation';
import { GetResponse, Row } from '@juvare/platform-shared/features/storage';
import React from 'react';

function getEntityTitle(row: Row<HanRoleColumns>): string {
    return row.columns.name;
}

const EntityDetailsComponent: React.FC<{ response: GetResponse<HanRoleData, HanRoleColumns> }> = (props) => {
    const { t } = usePlatformSdkReactTranslation();
    return <SlideoutFields fields={[{ label: 'Status', value: t(`classifiers.statusEnum.${props.response.data.status}`) }]} />;
};

const formConfig: WidgetFormConfig = {
    key: '!form',
    type: 'form',
    children: [
        {
            type: 'form.text',
            key: 'name',
            label: 'Name',
            validate: {
                required: true,
            },
        },
        {
            type: 'form.text',
            key: 'description',
            label: 'Description',
        },
        {
            type: 'form.select',
            key: 'status',
            label: 'Status',
            validate: {
                required: true,
            },
            values: [
                {
                    value: 'active',
                    label: 'Active',
                },
                {
                    value: 'deprecated',
                    label: 'Deprecated',
                },
                {
                    value: 'retired',
                    label: 'Retired',
                },
            ],
        },
    ],
};

const editFormConfig: WidgetFormConfig = {
    key: '!form',
    type: 'form',
    children: [
        {
            type: 'form.text',
            key: 'name',
            label: 'Name',
            validate: {
                required: true,
            },
        },
        {
            type: 'form.text',
            key: 'description',
            label: 'Description',
        },
        {
            type: 'form.select',
            key: 'status',
            label: 'Status',
            validate: {
                required: true,
            },
            values: [
                {
                    value: 'active',
                    label: 'Active',
                },
                {
                    value: 'deprecated',
                    label: 'Deprecated',
                },
                {
                    value: 'retired',
                    label: 'Retired',
                },
            ],
        },
    ],
};

const csvFileHeader = ['Name', 'Description', 'Status'];
const csvRowExample = ['Test', '', '{active|deprecated|retired}'];

function mapCsvRowToData(row: ClassifierAdminPageCsvRow<HanRoleData>): Promise<ClassifierAdminPageCsvRow<HanRoleData>> {
    const name = (row.data[0] ?? '').trim();
    const description = (row.data[1] ?? '').trim().toLowerCase();
    const status = (row.data[2] ?? '').trim().toLowerCase();
    if (name.length === 0) {
        row.errors.push('Name is required');
    }
    if (status.length === 0) {
        row.errors.push('Status is required');
    }
    switch (status) {
        case 'active':
        case 'deprecated':
        case 'retired':
            break;
        default:
            row.errors.push('Invalid status, expected one of: active, deprecated, retired');
    }
    if (row.errors.length === 0) {
        row.object = {
            data: {
                name,
                description,
                status: status as ClassifierStatus,
            },
        };
    }
    return Promise.resolve(row);
}

async function mapDataToCsvRow(response: GetResponse<HanRoleData, HanRoleColumns>): Promise<string[]> {
    return [response.data.name, response.data.description != null ? response.data.description : '', response.data.status];
}

export const HanRolesAdminPage: React.FC = () => {
    return (
        <ClassifierAdminPage
            entity={hanRoleEntityName}
            title='HAN Roles'
            fullTextSearchField='search'
            getEntityTitle={getEntityTitle}
            entityDetailsComponent={EntityDetailsComponent}
            newFormConfig={formConfig}
            editFormConfig={editFormConfig}
            csvFileHeader={csvFileHeader}
            csvRowExample={csvRowExample}
            mapCsvRowToData={mapCsvRowToData}
            mapDataToCsvRow={mapDataToCsvRow}
        />
    );
};
